import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  increment,
} from "firebase/firestore";
import "./FloatingAd.css";

function pickRandom(arr) {
  if (!arr || arr.length === 0) return null;
  return arr[Math.floor(Math.random() * arr.length)];
}

export default function FloatingAd() {
  const location = useLocation();
  const [ad, setAd] = useState(null);

  const [pos, setPos] = useState({ x: 20, y: 20 });

  const dragRef = useRef(null);
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const wasDragged = useRef(false);

  /* ------------------------------------------------------------
     Load advertiser
  ------------------------------------------------------------ */
  useEffect(() => {
    /* Detect page - moved inside useEffect */
    const detectPage = () => {
      const p = location.pathname;

      if (p === "/") return "home";
      if (p.startsWith("/trails/") && p !== "/trails") return "trailDetail";
      if (p === "/trails") return "trails";
      if (p === "/login") return "login";
      if (p === "/register") return "register";
      if (p === "/dashboard") return "dashboard";
      if (p.startsWith("/start-trail/")) return "startTrail";
      if (p === "/usefullinks") return "UsefulLinks";

      return null;
    };

    async function loadAd() {
      const page = detectPage();
      if (!page) return setAd(null);

      const q = query(
        collection(db, "advertisers"),
        where("isActive", "==", true),
        where("showOnPages", "array-contains", page)
      );

      const snap = await getDocs(q);
      const now = new Date();

      const ads = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((ad) => {
          if (!ad.activeUntil) return true;
          const expiry = ad.activeUntil.toDate
            ? ad.activeUntil.toDate()
            : new Date(ad.activeUntil);
          return expiry > now;
        });

      // auto-expire ads here
      for (const d of snap.docs) {
        const data = d.data();
        if (data.activeUntil) {
          const expiry = data.activeUntil.toDate
            ? data.activeUntil.toDate()
            : new Date(data.activeUntil);

          if (expiry <= now && data.isActive === true) {
            await updateDoc(doc(db, "advertisers", d.id), {
              isActive: false,
            });
          }
        }
      }

      const selected = pickRandom(ads);
      setAd(selected);

      // track view
      if (selected?.id) {
        try {
          await updateDoc(doc(db, "advertisers", selected.id), {
            views: increment(1),
          });
        } catch (err) {
          console.error("View update failed:", err);
        }
      }
    }

    loadAd();
  }, [location.pathname]);

  /* ------------------------------------------------------------
     Click handler (link only)
  ------------------------------------------------------------ */
  const handleAdClick = async () => {
    if (wasDragged.current) return;
    if (!ad?.id) return;

    try {
      await updateDoc(doc(db, "advertisers", ad.id), {
        clicks: increment(1),
      });
    } catch (err) {
      console.error("Click update failed:", err);
    }

    if (ad.linkUrl) {
      window.open(ad.linkUrl, "_blank", "noopener,noreferrer");
    }
  };

  /* ------------------------------------------------------------
     Drag behavior
  ------------------------------------------------------------ */
  const startDrag = (e) => {
    dragging.current = true;
    wasDragged.current = false;

    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;

    const rect = dragRef.current.getBoundingClientRect();
    offset.current = { x: cx - rect.left, y: cy - rect.top };
  };

  const onDrag = (e) => {
    if (!dragging.current) return;
    wasDragged.current = true;

    const cx = e.touches ? e.touches[0].clientX : e.clientX;
    const cy = e.touches ? e.touches[0].clientY : e.clientY;

    let newX = cx - offset.current.x;
    let newY = cy - offset.current.y;

    setPos({
      x: Math.max(0, Math.min(newX, window.innerWidth - 200)),
      y: Math.max(0, Math.min(newY, window.innerHeight - 140)),
    });
  };

  const endDrag = () => {
    dragging.current = false;
  };

  useEffect(() => {
    document.addEventListener("mousemove", onDrag);
    document.addEventListener("mouseup", endDrag);

    document.addEventListener("touchmove", onDrag, { passive: false });
    document.addEventListener("touchend", endDrag);

    return () => {
      document.removeEventListener("mousemove", onDrag);
      document.removeEventListener("mouseup", endDrag);
      document.removeEventListener("touchmove", onDrag);
      document.removeEventListener("touchend", endDrag);
    };
  });

  if (!ad) return null;

  return (
    <div
      className="floating-ad"
      ref={dragRef}
      onMouseDown={startDrag}
      onTouchStart={startDrag}
      style={{
        left: pos.x,
        top: pos.y,
      }}
    >
      <p className="floating-ad-label">Advert (Click Image to Move)</p>

      {/* banner is NOT clickable */}
      <img
        src={ad.imageUrl}
        alt={ad.name}
        className="floating-ad-banner"
        style={{ cursor: "move" }}
        onClick={(e) => e.stopPropagation()}
      />

      {/* ONLY the link is clickable */}
      {ad.linkUrl && (
        <div style={{ textAlign: "center", marginTop: "10px" }}>
          <a
            href={ad.linkUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="floating-ad-link clean-link"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleAdClick();
            }}
          >
            View Advertiser
          </a>
        </div>
      )}
    </div>
  );
}